import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';

import { LoadingComponentModule, MoteMapMarkerModule, ToggleMapStyleModule } from '@motes-sdk/components';
import { ContainerOptions, ContainerType, Coords, Mote, MoteEvent, MoteEventType, MoteSortType, MotesPaths, MoteType } from '@motes-sdk/models';
import { AutoSub, AutoSubOnDestroy, MapService, MoteService, NewStoryInfo, ProjectService } from '@motes-sdk/providers';
import { MotesLoginService, UserRights } from '@motes-sdk/motes-login';
import { Animations } from '@motes-sdk/animations';
import { UploadMediaService } from '@motes-sdk/components/upload-media/upload-media.service';
import { ClickOutsideDirectiveModule } from '@motes-sdk/directives';

import { MapSearchBarComponent } from '../../pages/editor-page/components/map-search-bar/map-search-bar.component';
import { SelectTourTypeComponent } from '../../pages/editor-page/components/select-tour-type/select-tour-type.component';
import { WaypointTooltipComponent } from '../../pages/editor-page/components/waypoint-tooltip/waypoint-tooltip.component';
import { WaypointHelpComponent } from '../../pages/editor-page/components/waypoint-help/waypoint-help.component';
import { MapEditorService } from '../../services';
import { StoryCreatedEvent, StoryDragEndEvent, StoryDragStartEvent } from '../../models/map-editor-event';
import { MapOverlayService } from '../../services/map-overlay.service';


@AutoSubOnDestroy()
@Component({
    selector: 'map-overlay',
    standalone: true,
    imports: [CommonModule, LoadingComponentModule, MapSearchBarComponent, ToggleMapStyleModule, SelectTourTypeComponent, MoteMapMarkerModule, WaypointHelpComponent, WaypointTooltipComponent, ClickOutsideDirectiveModule],
    templateUrl: './map-overlay.component.html',
    styleUrls: ['./map-overlay.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.fadeIn(200)]
})

export class MapOverlayComponent implements OnInit {

    @Input() showMapTools: boolean = true;
    @Output() changeActiveStory = new EventEmitter<Mote>();

    private userRights: UserRights;
    public stories: Mote[];
    public tour: Mote;
    public activeStoryKey: string;
    public creatingStory: boolean = false;
    public mapIsBlocked: boolean = false;

    // Expose to view
    public MoteType = MoteType;
    public MoteSortType = MoteSortType;

    constructor(
        private moteService: MoteService,
        public mapService: MapService,
        private projectService: ProjectService,
        private mapEditorService: MapEditorService,
        private loginService: MotesLoginService,
        private uploadMediaService: UploadMediaService,
        private mapOverlayService: MapOverlayService
    ) { }

    ngOnInit(): void {

        AutoSub(this).reg['activeToolSub'] = this.mapOverlayService.watchActiveTool().subscribe((activeTool) => {
            this.mapIsBlocked = Boolean(activeTool);
        })

        AutoSub(this).reg['tourSub'] = this.projectService.watchActiveTourDetails().subscribe((tourDetails) => {
            this.tour = tourDetails?.tour;
            this.stories = tourDetails?.children || [];
        });

        AutoSub(this).reg['storySub'] = this.projectService.watchActiveStory().subscribe((story) => {
            this.activeStoryKey = story?.key;
        })

        AutoSub(this).reg['userRightsSub'] = this.loginService.watchUserRights().subscribe((userRights) => {
            this.userRights = userRights;
        });
    }

    public async addStory(storyInfo: NewStoryInfo) {
        if(!this.tour) return;
        this.creatingStory = true;
        const stories = this.stories;
        const previousStory = stories[stories.length - 1];
        const newStory = await this.moteService.createStory(
            storyInfo.title,
            storyInfo.addressInfo,
            storyInfo.coords,
            this.tour,
            stories,
            this.userRights.uid,
            storyInfo.placeId,
            storyInfo.placeIdName,
            storyInfo.placeIdIsAttraction,
            storyInfo.placeIdCheckedOn
        );

        // Save auto generated story image
        if(storyInfo.image) this.uploadStoryImage(newStory, storyInfo.image);

        // Create a map editor event with the new story and its connected sibling
        this.mapEditorService.sendMapEditorEvent(new StoryCreatedEvent(newStory, previousStory));
        this.creatingStory = false;
        // TODO: Consider to remove this and do the navigation after creating the story here
        this.navigateToStory(newStory);
    }

    private uploadStoryImage(story: Mote, image: File ) : Promise<void> {
        const media = story.image;
        const mediaFile = story.image.original;
        // Set storage path of origin image
        const [_, fileExt] = image.name.split(".");
        mediaFile.storage = MotesPaths.MOTE_MEDIA_STORAGE_PATH(story.project, story.image.key, story.image, fileExt);
        // Set origin media file blob to be street view image file
        mediaFile.blob = image;
        // Mark media as auto generated (to be able to regenerate street view image on relocate)
        media.isAutoGenerated = true;
        // Use default image formats
        const format = UploadMediaService.DefaultImageFormat;
        // Create media container
        const container: ContainerOptions = { type: ContainerType.MOTE, containerKey: story.key, mediaKey: media.key };
        // Upload the image to story
        return this.uploadMediaService.uploadMedia(media, mediaFile, null, format, { IMAGE: true }, container);
    }

    public async updateStoryImage(update: { story: Mote, image: File }) {
        if(!update.story || !update.image) return console.error("No story or image given, can not update story image");
        this.uploadStoryImage(update.story, update.image);
    }

    public navigateToStory(story: Mote) {
        this.changeActiveStory.emit(story);
    }

    public markerMoteEvent(ev: MoteEvent) {
        if(ev.type == MoteEventType.MAP_MOTE_SELECTED) {
            this.navigateToStory(ev.mote);
        }
    }

    public markerDragStart(ev: { moteKey: string }) {
        this.mapEditorService.sendMapEditorEvent(new StoryDragStartEvent(ev.moteKey));
    }

    public markerDragEnd(ev: { moteKey: string, oldPosition: Coords, newPosition: Coords }) {
        this.mapEditorService.sendMapEditorEvent(new StoryDragEndEvent(ev.moteKey, ev.oldPosition, ev.newPosition));
    }

    public trackByUpdate(index: number, story: Mote) {
        return story.key + story.position.lat + story.position.long + story.image.thumb.url;
    }

    public unblockMap() {
        this.mapOverlayService.updateActiveTool(false, null);
    }

    public outsideClick() {
        if(this.mapIsBlocked) this.unblockMap();
    }

}
